import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { Category } from '../types';
import toast from 'react-hot-toast';
import logo from '../assets/images/ruyo-logo.webp';
import texture from '../assets/images/header-texture.webp';
import { SupportDialog } from '../components/SupportDialog';
import { usePageScroll } from '../hooks/usePageScroll';

export const Home: React.FC = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [isSupportOpen, setIsSupportOpen] = useState(false);
    const needsRefresh = useRef(false);

    // Home.tsx - fetchRootCategories
    const fetchRootCategories = async () => {
        try {
            setLoading(true);

            // ПРОСТО ДЕЛАЕМ ЗАПРОС - без всяких ожиданий
            // Если нет сессии, RLS все равно разрешает чтение (политики SELECT true)
            const { data, error } = await supabase
                .from('categories')
                .select('*')
                .eq('is_active', true)
                .is('parent_id', null)
                .order('name');

            console.log('🏠 Categories loaded:', {
                count: data?.length,
                error: error?.message,
                hasSession: !! (await supabase.auth.getSession()).data.session
            });

            if (error) throw error;
            setCategories(data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Ошибка загрузки категорий');
        } finally {
            setLoading(false);
        }
    };

    usePageScroll('home', () => {
        console.log('📱 Home активирована из кэша');
        if (needsRefresh.current) {
            console.log('🔄 Обновляем категории на Home');
            fetchRootCategories();
        }
    });

    useEffect(() => {
        const preloadImages = async () => {
            const images = [logo, texture];
            const promises = images.map((src) => new Promise((resolve, reject) => {
                const img = new Image();
                img.src = src;
                img.onload = resolve;
                img.onerror = reject;
            }));
            try {
                await Promise.all(promises);
                setImagesLoaded(true);
            } catch {
                setImagesLoaded(true);
            }
        };
        preloadImages();
    }, []);

    useEffect(() => {
        fetchRootCategories();
    }, []);

    const handleCategoryClick = (categoryId: number) => {
        navigate(`/category/${categoryId}`);
    };

    return (
        <div className="home-container">
            {(loading || !imagesLoaded) && (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p style={{ marginTop: '16px', color: '#6b7280' }}>Загрузка...</p>
                </div>
            )}

            {!loading && imagesLoaded && (
                <>
                    <div className="hero-section" style={{ backgroundImage: `url(${texture})` }}>
                        <img className='logo' src={logo} alt="Logo" draggable="false"/>
                    </div>

                    <div className="categories-section">
                        <h2 className="section-title">
                            <span>Категории</span>
                            <button className="support-button" onClick={() => setIsSupportOpen(true)}>
                                🛠️ Техподдержка
                            </button>
                        </h2>
                        {categories.length === 0 ? (
                            <div className="empty-state"><p>Категории не найдены</p></div>
                        ) : (
                            <div className="categories-grid-home">
                                {categories.map((category) => (
                                    <div
                                        key={category.id}
                                        onClick={() => handleCategoryClick(category.id)}
                                        className="category-card-home"
                                    >
                                        <div className="category-image-home">
                                            <img
                                                src={category.image_url || `https://via.placeholder.com/400x300?text=${encodeURIComponent(category.name)}`}
                                                alt={category.name}
                                                className="category-img"
                                                loading="lazy"
                                            />
                                        </div>
                                        <h3 className="category-name-home">{category.name}</h3>
                                        {category.description && (
                                            <p className="category-desc-home">{category.description}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            {isSupportOpen && <SupportDialog onClose={() => setIsSupportOpen(false)} />}
        </div>
    );
};
